import type { ArgsFor } from 'ember-modifier';
import Modifier from 'ember-modifier';

import { registerDestructor } from '@ember/destroyable';
import { scheduleOnce } from '@ember/runloop';
import type Owner from '@ember/owner';

interface IAutoFocusSignature {
  Element: HTMLElement;
  Args: {
    Named: Record<string, never>;
    Positional: [boolean] | [boolean, string];
  };
}

export default class AutofocusModifier extends Modifier<IAutoFocusSignature> {
  elementToFocus: HTMLInputElement | null = null;

  constructor(owner: Owner, args: ArgsFor<IAutoFocusSignature>) {
    super(owner, args);

    registerDestructor(this, () => {
      this.elementToFocus?.blur();
    });
  }

  modify(element: Element | null, [active, selector]: [boolean, string]) {
    if (!active || !element) {
      return;
    }

    (document.activeElement as HTMLElement | null)?.blur();
    const childElement: HTMLElement | null = element.querySelector(
      selector ?? 'input:not([disabled])'
    );
    this.elementToFocus = (childElement ?? element) as HTMLInputElement;

    // eslint-disable-next-line ember/no-runloop
    scheduleOnce('afterRender', this, this.focusElement);
  }

  focusElement() {
    this.elementToFocus?.focus();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    autofocus: typeof AutofocusModifier;
  }
}
